import api, { API_BASE_URL } from '../api/apiClient';

const DOCUSTOR_BASE_URL = 'docustor';

// ===================================
// HELPER: GENERATE FOLDER UUID
// ===================================

/**
 * Generate a timestamp-based UUID for folders
 * Returns int64 as string (Unix milliseconds)
 */
export const generateFolderUUID = (): string => {
  return Date.now().toString();
};

// ===================================
// TYPES
// ===================================

// V2 Folder Types
export type FolderType = 'PRIVATE' | 'ORGANIZATION' | 'RELATION';

export interface RolePermission {
  role_type: string;
  can_create: boolean;
  can_read: boolean;
  can_update: boolean;
  can_delete: boolean;
}

export interface CreateFolderPayload {
  folder_uuid: string; // Generated UUID as string
  name: string;
  description: string;
  allowed_extensions: string[];
  max_file_size_mb: number;
  is_private?: boolean; // Legacy flag, try to use folder_type instead
  folder_type: FolderType; // V2: Required field
  rec_user_id?: number; // V2: Required for RELATION
  permissions?: RolePermission[]; // Optional - if not provided, uses default permissions
  parent_folder_id?: string; // V2: Optional for subfolders
}

export interface UpdateFolderPayload {
  name: string;
  description: string;
  allowed_extensions: string[];
  max_file_size_mb: number;
}

// ===================================
// API CALLS
// ===================================

/**
 * Get all root folders (V2)
 * GET /docustor/folders
 */
export const getDocustorFolders = async () => {
  const response = await api.get(`${DOCUSTOR_BASE_URL}/folders`);
  return response.data;
};

/**
 * Get child folders (V2)
 * GET /docustor/folders/:folder_uuid/children
 */
export const getFolderChildren = async (folderUUID: string) => {
  const response = await api.get(`${DOCUSTOR_BASE_URL}/folders/${folderUUID}/children`);
  return response.data; // Expected: { success: true, data: [...] }
};

/**
 * Get documents in a specific folder (V2)
 * GET /docustor/folders/:folder_uuid/documents
 */
export const getFolderDocuments = async (folderUUID: string) => {
  const response = await api.get(`${DOCUSTOR_BASE_URL}/folders/${folderUUID}/documents`);
  // Expected: { success: true, data: [...] } where data is array of documents
  return response.data;
};

/**
 * Create new folder with optional custom permissions
 * POST /docustor/folders (V2)
 * 
 * Automatically generates folder_uuid if not provided.
 */
export const createDocustorFolder = async (folderData: Omit<CreateFolderPayload, 'folder_uuid'> & { folder_uuid?: string }) => {
  // Generate folder_uuid if not provided
  const payload: CreateFolderPayload = {
    ...folderData,
    folder_uuid: folderData.folder_uuid || generateFolderUUID(),
    // V2: Derive folder_type if not provided
    folder_type: folderData.folder_type || (folderData.is_private ? 'PRIVATE' : 'ORGANIZATION'),
  };

  const response = await api.post(`${DOCUSTOR_BASE_URL}/folders`, payload);
  return response.data.data || response.data;
};

/**
 * Create a RELATION folder
 * POST /docustor/folders/relation
 */
export const createRelationFolder = async (name: string, recUserId: number) => {
  const payload = {
    folder_uuid: generateFolderUUID(),
    name,
    rec_user_id: recUserId
  };
  const response = await api.post(`${DOCUSTOR_BASE_URL}/folders/relation`, payload);
  return response.data.data || response.data;
};

/**
 * Create a PRIVATE subfolder inside a RELATION folder
 * POST /docustor/folders/relation/:folder_uuid/private
 */
export const createPrivateSubFolder = async (parentFolderUUID: string, name: string, is_private?: boolean) => {
  const payload = {
    folder_uuid: generateFolderUUID(),
    name,
    is_private: is_private ?? true
  };
  const response = await api.post(`${DOCUSTOR_BASE_URL}/folders/relation/${parentFolderUUID}/private`, payload);
  return response.data.data || response.data;
};

/**
 * Get permissions for a specific folder
 * GET /docustor/folders/:folder_uuid/permissions
 */
export const getFolderPermissions = async (folderUUID: string) => {
  const response = await api.get(`${DOCUSTOR_BASE_URL}/folders/${folderUUID}/permissions`);
  return response.data;
};

/**
 * Update permissions for a specific folder (bulk update)
 * PUT /docustor/folders/:folder_uuid/permissions
 */
export const updateFolderPermissions = async (
  folderUUID: string,
  permissions: RolePermission[]
) => {
  const response = await api.put(
    `${DOCUSTOR_BASE_URL}/folders/${folderUUID}/permissions`,
    { permissions }
  );
  return response.data;
};

/**
 * Delete a specific role's permission from a folder
 * DELETE /docustor/folders/:folder_uuid/permissions/:role_type
 */
export const deleteFolderPermission = async (
  folderUUID: number,
  roleType: string
) => {
  const response = await api.delete(
    `${DOCUSTOR_BASE_URL}/folders/${folderUUID}/permissions/${roleType}`
  );
  return response.data;
};

export const uploadDocumentFile = async (
  folderUUID: string,
  file: File,
  onUploadProgress?: (progressEvent: any) => void
) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(
    `${DOCUSTOR_BASE_URL}/folders/${folderUUID}/documents`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent: any) => {
        if (onUploadProgress && progressEvent.total) {
          onUploadProgress(progressEvent);
        }
      },
    }
  );

  // V2 returns { success: true, data: { ... } }
  // Unwrapping it so downstream consumers get the document object directly
  return response.data.data || response.data;
};

export const deleteDocustorDoc = async (id: string, fid: string) => {
  // Use V2 endpoint if available, but for now map to what handler expects
  // Handler: DELETE /api/v1/docustor/documents/:doc_uuid
  const response = await api.delete(`${DOCUSTOR_BASE_URL}/documents/${id}`);
  return response.data;
}

export const saveDocumentMetadata = async (
  folderUUID: number,
  roleType: string
) => {
  const response = await api.delete(
    `${DOCUSTOR_BASE_URL}/folders/${folderUUID}/permissions/${roleType}`
  );
  return response.data;
};

export const updateDocustorFolder = async (
  folderUUID: string,
  folderData: UpdateFolderPayload
) => {
  // Handler: PUT /api/v1/docustor/folders/:folder_uuid
  const response = await api.put(`${DOCUSTOR_BASE_URL}/folders/${folderUUID}`, folderData);
  return response.data;
};

/**
 * Delete folder
 * DELETE /docustor/folders/:folder_uuid
 */
export const deleteDocustorFolder = async (folderUUID: string) => {
  const response = await api.delete(`${DOCUSTOR_BASE_URL}/folders/${folderUUID}`);
  return response.data;
};


// ===================================
// DOCUMENT PREVIEW & DOWNLOAD
// ===================================

/**
 * Get document preview URL (streams through your server)
 * Returns full URL that can be used in <img>, <iframe>, or window.open()
 */
export const getDocumentPreviewUrl = (docUUID: string) => {
  // Handler: GET /api/v1/docustor/documents/:doc_uuid/preview
  // Assuming apiClient.defaults.baseURL is set, but this returns full URL string for img tag
  // Adjust base URL if needed. Assuming relative to API root.
  // Warning: This hardcoded localhost might be an issue in prod.
  // Ideally, use a relative path if the frontend and backend are on same domain, or env var.
  // For now, keeping consistent with previous code but updating path.
  return `${API_BASE_URL}/${DOCUSTOR_BASE_URL}/documents/${docUUID}/preview`;
};

/**
 * Get document download URL (forces download through your server)
 */
export const getDocumentDownloadUrl = (docUUID: string | string | null): string => {
  if (!docUUID) return '';
  return `${API_BASE_URL}/${DOCUSTOR_BASE_URL}/documents/${docUUID}/download`;
};

/**
 * Get document thumbnail URL (for hover previews)
 */
export const getDocumentThumbnailUrl = (docUUID: string | string | null): string => {
  if (!docUUID) return '';
  return `${API_BASE_URL}/${DOCUSTOR_BASE_URL}/documents/${docUUID}/thumbnail`;
};

/**
 * Download document (programmatically trigger download)
 */
export const downloadDocument = async (docUUID: string | string, filename: string) => {
  try {
    const response = await api.get(
      `${DOCUSTOR_BASE_URL}/documents/${docUUID}/download`,
      {
        responseType: 'blob',
      }
    );

    // Create blob URL and trigger download
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};


// ===================================
// TRANSFORMED TYPES (UI FRIENDLY)
// ===================================

export interface DocustorFolderUI {
  folder_id: string; // The UUID string
  folder_uuid: string; // Legacy field, same as folder_id
  name: string;
  description?: string; // Added description field
  document_count: number;
  allowed_extensions?: string[];
  max_file_size_mb?: number;
  is_private?: boolean;
  folder_type: FolderType; // V2
  created_by?: number;     // V2
  rec_user_id?: number | null; // V2
  parent_folder_id?: string | null; // V2
}

export interface DocustorDocumentUI {
  doc_id: string;
  name: string;
  folder_id: string;
  file_type: string;
  file_extension: string;
  file_size_bytes: number;
  created_at?: string;
  created_by?: string;
}

// ===================================
// INTERNAL HELPERS
// ===================================

const decodeBase64Documents = <T = any>(value?: string): T[] => {
  if (!value) return [];
  try {
    return JSON.parse(atob(value));
  } catch {
    return [];
  }
};

const unwrapNullable = (v: any): string | undefined => {
  if (v && typeof v === 'object' && 'String' in v) {
    return v.Valid ? v.String : undefined;
  }
  return typeof v === 'string' ? v : undefined;
};

// ===================================
// ADAPTER: DOCUSTOR → UI FORMAT
// ===================================

export const getDocustorFoldersForDocumentsPage = async (): Promise<{
  folders: DocustorFolderUI[];
  documents: DocustorDocumentUI[];
}> => {
  const raw = await getDocustorFolders();

  const folders: DocustorFolderUI[] = [];
  const documents: DocustorDocumentUI[] = [];

  for (const f of raw.data ?? []) {
    // ---- folders ----

    folders.push({
      folder_id: String(f.id), // Correctly map to Numeric ID
      folder_uuid: String(f.folder_uuid), // Correctly map to UUID
      name: f.name || f.folder_name,
      description: f.description, // Map description
      document_count: f.document_count ?? 0,
      allowed_extensions: f.allowed_extensions ?? [],
      max_file_size_mb: f.max_file_size_mb ?? 10,
      is_private: f.is_private ?? (f.folder_type === 'PRIVATE'),
      folder_type: f.folder_type || (f.is_private ? 'PRIVATE' : 'ORGANIZATION'),
      created_by: f.created_by,
      rec_user_id: f.rec_user_id?.Valid ? f.rec_user_id.Int64 : null,
      parent_folder_id: f.parent_folder_id?.Valid ? String(f.parent_folder_id.Int64) : null,
    });

    // ---- documents (base64 decoded) ----
    // Note: V2 might return documents directly or filtered. 
    // If getting root folders, documents might not be included for all folders anymore
    // or strictly for that top level. 
    // Assuming backend still returns documents for backward compat if legacy query used, 
    // OR this function adapts the new response.
    // If V2 `ListRootFolders` doesn't return docs (likely), this array will be empty.
    // That's fine for the Tree View approach where we fetch docs on click.
    const docs = f.documents ? decodeBase64Documents<any>(f.documents) : [];

    for (const d of docs) {
      documents.push({
        doc_id: d.doc_uuid,
        name: d.document_name ?? d.original_filename,
        folder_id: String(f.folder_uuid),
        file_type: unwrapNullable(d.file_extension) ?? d.file_type,
        file_extension: unwrapNullable(d.file_extension) ?? '',
        file_size_bytes: d.file_size_bytes ?? 0,
        created_at: d.document_created_at,
        created_by: d.created_by,
      });
    }
  }

  return { folders, documents };
};

// ===================================
// DOCUMENT UPLOAD (UI WRAPPER)
// ===================================

export interface UploadDocumentParams {
  folderUUID: string;
  file: File;
  customName?: string;
  onUploadProgress?: (progressEvent: any) => void;
}

/**
 * Upload document with optional rename
 * Returns UI-ready document object
 */
export const uploadDocumentForDocumentsPage = async ({
  folderUUID,
  file,
  customName,
  onUploadProgress,
}: UploadDocumentParams) => {
  let fileToUpload = file;

  // Handle optional rename (same logic as old page)
  if (customName && customName.trim() && customName !== file.name) {
    const originalExtension = file.name.split('.').pop()?.toLowerCase() || '';
    const hasExtension = customName.includes('.');
    const finalFileName = hasExtension
      ? customName
      : `${customName}.${originalExtension}`;

    const blob = file.slice(0, file.size, file.type);
    fileToUpload = new File([blob], finalFileName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  }

  // Upload via existing Docustor API
  const uploadedDoc = await uploadDocumentFile(
    folderUUID,
    fileToUpload,
    onUploadProgress
  );

  /**
   * IMPORTANT:
   * Map the backend response to the UI model (DocustorDocumentUI)
   * to ensure the optimistic update has the correct shape.
   */
  return {
    doc_id: uploadedDoc.doc_uuid ?? uploadedDoc.id ?? uploadedDoc.document_id,
    name: uploadedDoc.document_name ?? uploadedDoc.name ?? uploadedDoc.original_filename ?? file.name,
    folder_id: String(folderUUID),
    file_type: unwrapNullable(uploadedDoc.file_extension) ?? uploadedDoc.file_type ?? '',
    file_extension: unwrapNullable(uploadedDoc.file_extension) ?? '',
    file_size_bytes: uploadedDoc.file_size_bytes ?? file.size,
    created_at: uploadedDoc.document_created_at ?? uploadedDoc.created_at ?? new Date().toISOString(),
  } as DocustorDocumentUI;
};

export const renameDocustorFolder = async (
  folder: {
    folder_id: string;
    name: string;
    description?: string;
    allowed_extensions?: string[];
    max_file_size_mb?: number;
  },
  newName: string
) => {
  return updateDocustorFolder(folder.folder_id, {
    name: newName,
    description: folder.description ?? '',
    allowed_extensions: folder.allowed_extensions ?? [],
    max_file_size_mb: folder.max_file_size_mb ?? 20, // safe default
  });
};

export const createDocustorFolderWithDefaults = async (name: string) => {
  return createDocustorFolder({
    folder_uuid: generateFolderUUID(),
    name,
    description: '',
    allowed_extensions: ['pdf'],
    max_file_size_mb: 20,
    folder_type: 'ORGANIZATION', // Default to Organization
    permissions: [
      {
        role_type: 'HRMANAGER',
        can_create: true,
        can_read: true,
        can_update: true,
        can_delete: true,
      },
      {
        role_type: 'MANAGER',
        can_create: true,
        can_read: true,
        can_update: false,
        can_delete: false,
      },
      {
        role_type: 'EXECUTIVE',
        can_create: false,
        can_read: true,
        can_update: false,
        can_delete: false,
      },
      {
        role_type: 'EMPLOYEE',
        can_create: false,
        can_read: true,
        can_update: false,
        can_delete: false,
      },
    ],
  });
};

/**
 * Create a private folder ("My Folder") - only the owner can access
 * No role-based permissions are applied
 */
export const createPrivateFolderWithDefaults = async (name: string) => {
  return createDocustorFolder({
    folder_uuid: generateFolderUUID(),
    name,
    description: 'Private folder',
    allowed_extensions: ['pdf', 'docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'jpg', 'jpeg', 'png', 'zip'],
    max_file_size_mb: 20,
    is_private: true,
    folder_type: 'PRIVATE',
    // No permissions needed - only owner can access
  });
};

/**
 * Get the current user's permissions for a specific folder (V2)
 * GET /docustor/v2/:folder_id/permissions
 * Returns permissions based on the logged-in user's role only
 */
export const getFolderPermissionForTheUser = async (folderId: string) => {
  const response = await api.get(`${DOCUSTOR_BASE_URL}/v2/${folderId}/permissions`);
  return response.data;
};

// ===================================
// USER DOCS: MOVE FILE
// ===================================

export const moveFile = async (
  docId: string,
  payload: { target_folder_id: string }
) => {
  const response = await api.put(
    `user-docs/files/${docId}/move`,
    payload
  );
  return response.data;
};
